import { baseApi } from "./baseApi";
import type { Role } from "../../../shared/roles";
import { getUsersApi } from "../platform/platformService";
import type { ICurrentUser } from "../../views/settings/security/security.interface";

// Types for RBAC API — Phase 2 (scope-based, no overrides)

/**
 * Role as returned by GET /admin/rbac/roles.
 * In Phase 2, permissions/navigation live directly on the role — no override layer.
 */
export interface RoleFromApi {
  id: string; // role name used as ID (e.g. "marketing_manager")
  name: string;
  label: string;
  description: string;
  category: string;
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
  isSystem: boolean;
  isImmutable: boolean;
  isHidden?: boolean;
  hasOverrides: false; // always false in Phase 2 — kept for UI compat
}

/**
 * User assignment as returned from the assignments sub-array in GET /admin/rbac/users
 */
export interface UserAssignment {
  id: string;
  roleName: string;
  roleLabel: string;
  tenantId: string | null;
  instanceId: string | null;
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
  assignedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * User as returned by GET /admin/rbac/users
 */
export interface RbacUser {
  email: string;
  displayName: string | null;
  allowedInstances: string[] | null;
  role: string; // primary role name (legacy compat field)
  assignments: UserAssignment[];
  roleOverride: null; // always null in Phase 2
  isBlocked: boolean; // true if user has profile but no assignments
}

export interface AccessMatrixUser {
  email: string;
  role: string;
  roleLabel: string;
  scope: "GLOBAL" | "TENANT" | "INSTANCE";
  instanceId: string | null;
  tenantId: string | null;
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
  permissionCount: number;
  navigationCount: number;
  hasRoleConfigOverride: false;
  hasUserOverride: false;
}

export interface MyPermissions {
  role: Role;
  roleLabel: string;
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
  allowedInstances: string[] | null;
  instanceScope: "all" | "assigned" | "single";
}

// Request types
export interface UpdateRoleRequest {
  roleId: string;
  permissions?: Record<string, boolean>;
  navigation?: Record<string, boolean>;
}

export interface CreateUserAssignmentRequest {
  email: string;
  role: Role;
  instanceId?: string | null;
  tenantId?: string | null;
  firstName?: string;
  lastName?: string;
}

export interface UpdateUserAssignmentRequest {
  email: string;
  role?: Role;
  instanceId?: string | null;
  tenantId?: string | null;
}

export interface CreateRoleRequest {
  name: string;
  label: string;
  description?: string;
  category?: string;
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
}

export const rbacApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Role Management
    getRoles: builder.query<{ roles: RoleFromApi[] }, { instanceId?: string } | void>({
      query: (params) => ({
        url: "/admin/rbac/roles",
        params: params ? { instanceId: params.instanceId } : undefined,
      }),
      providesTags: (result, error, arg) => [
        { type: "RBAC", id: "ROLES" },
        ...(arg?.instanceId ? [{ type: "RBAC" as const, id: `ROLES-${arg.instanceId}` }] : []),
      ],
    }),

    createRole: builder.mutation<{ role: RoleFromApi }, CreateRoleRequest>({
      query: (body) => ({
        url: "/admin/rbac/roles",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "RBAC", id: "ROLES" }],
    }),

    deleteRole: builder.mutation<{ success: boolean; message: string }, string>({
      query: (roleId) => ({
        url: `/admin/rbac/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "RBAC", id: "ROLES" },
        { type: "RBAC", id: "MATRIX" },
      ],
    }),

    updateRole: builder.mutation<{ roleConfig: unknown }, UpdateRoleRequest>({
      query: ({ roleId, ...body }) => ({
        url: `/admin/rbac/roles/${roleId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "RBAC", id: "ROLES" },
        { type: "RBAC", id: "MATRIX" },
        { type: "RBAC", id: "MY_PERMISSIONS" },
      ],
    }),

    // User Management
    getUsers: builder.query<{ users: RbacUser[] }, { instanceId: string }>({
      query: ({ instanceId }) => `/admin/rbac/users?instanceId=${encodeURIComponent(instanceId)}`,
      providesTags: [{ type: "RBAC", id: "USERS" }],
    }),

    createUserAssignment: builder.mutation<
      { profile: unknown },
      CreateUserAssignmentRequest & { instanceId: string }
    >({
      query: ({ instanceId, ...body }) => ({
        url: `/admin/rbac/users?instanceId=${encodeURIComponent(instanceId)}`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
        { type: "RBAC", id: "MY_PERMISSIONS" },
        // SW-ADMIN-2.4 cross-wire: a global-role / block change surfaces in the
        // unified directory's platform-role + status columns → bust it too.
        "CrmUserAdmin",
      ],
    }),

    updateUserAssignment: builder.mutation<
      { profile: unknown; roleOverride: null },
      UpdateUserAssignmentRequest & { instanceId: string }
    >({
      query: ({ email, instanceId, ...body }) => ({
        url: `/admin/rbac/users/${encodeURIComponent(email)}?instanceId=${encodeURIComponent(instanceId)}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
        { type: "RBAC", id: "MY_PERMISSIONS" },
        // SW-ADMIN-2.4 cross-wire: a global-role / block change surfaces in the
        // unified directory's platform-role + status columns → bust it too.
        "CrmUserAdmin",
      ],
    }),

    deleteUserAssignment: builder.mutation<
      { success: boolean },
      { email: string; instanceId: string }
    >({
      query: ({ email, instanceId }) => ({
        url: `/admin/rbac/users/${encodeURIComponent(email)}?instanceId=${encodeURIComponent(instanceId)}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
        { type: "RBAC", id: "MY_PERMISSIONS" },
        // SW-ADMIN-2.4 cross-wire: a global-role / block change surfaces in the
        // unified directory's platform-role + status columns → bust it too.
        "CrmUserAdmin",
      ],
    }),

    // Access Matrix
    getAccessMatrix: builder.query<{ accessMatrix: AccessMatrixUser[] }, { instanceId: string }>({
      query: ({ instanceId }) =>
        `/admin/rbac/access-matrix?instanceId=${encodeURIComponent(instanceId)}`,
      providesTags: [{ type: "RBAC", id: "MATRIX" }],
    }),

    // Current User Permissions
    getMyPermissions: builder.query<MyPermissions, { instanceId?: string } | void>({
      query: (params) => ({
        url: "/admin/rbac/my-permissions",
        params: params ? { instanceId: params.instanceId } : undefined,
      }),
      providesTags: (result, error, arg) => [
        { type: "RBAC", id: "MY_PERMISSIONS" },
        ...(arg?.instanceId
          ? [{ type: "RBAC" as const, id: `MY_PERMISSIONS-${arg.instanceId}` }]
          : []),
      ],
    }),

    // Block/Unblock — calls existing /admin/users endpoints
    blockUser: builder.mutation<
      { isSuccessful: boolean; message: string },
      { email: string; instanceId: string }
    >({
      query: ({ email, instanceId }) => ({
        url: `/admin/users/${encodeURIComponent(email)}/block?instanceId=${encodeURIComponent(instanceId)}`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
        { type: "RBAC", id: "MY_PERMISSIONS" },
        // SW-ADMIN-2.4 cross-wire: a global-role / block change surfaces in the
        // unified directory's platform-role + status columns → bust it too.
        "CrmUserAdmin",
      ],
    }),

    unblockUser: builder.mutation<
      { isSuccessful: boolean; message: string },
      { email: string; instanceId: string }
    >({
      query: ({ email, instanceId }) => ({
        url: `/admin/users/${encodeURIComponent(email)}/unblock?instanceId=${encodeURIComponent(instanceId)}`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
        { type: "RBAC", id: "MY_PERMISSIONS" },
        // SW-ADMIN-2.4 cross-wire: a global-role / block change surfaces in the
        // unified directory's platform-role + status columns → bust it too.
        "CrmUserAdmin",
      ],
    }),

    // Platform Users (for merging with RBAC data in the Manage Users tab)
    getPlatformUsers: builder.query<{ users: ICurrentUser[] }, void>({
      queryFn: async () => {
        try {
          const response = await getUsersApi();
          return { data: response };
        } catch (error) {
          return { error: { status: 500, data: error } };
        }
      },
      providesTags: [{ type: "RBAC", id: "PLATFORM_USERS" }],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useUpdateRoleMutation,
  useGetUsersQuery,
  useCreateUserAssignmentMutation,
  useUpdateUserAssignmentMutation,
  useDeleteUserAssignmentMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
  useGetAccessMatrixQuery,
  useGetMyPermissionsQuery,
  useGetPlatformUsersQuery,
} = rbacApi;
